import { dayjs } from "@/utils/dayjs";
import { Dayjs, isDayjs } from "dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { ECaseMatchStatus, EXPIRY_STATUS } from "@/types/enum";

export const isDateSameOrAfter = (value: Dayjs, refDate: Dayjs): boolean => {
  return dayjs(value).format(DATE_FORMAT) >= dayjs(refDate).format(DATE_FORMAT);
};

export const createMinDateTest = (refFieldKey: string, refFieldLabel: string) => ({
  name: "min-date",
  message: `${refFieldLabel}、またはそれ以降の日付を入力してください。`,
  test: function (this: { parent: Record<string, unknown> }, value: unknown) {
    const refDate = this.parent[refFieldKey];
    if (!isDayjs(value) || !isDayjs(refDate)) return true;
    return isDateSameOrAfter(value, refDate);
  },
});

export const hasClassificationMismatch = (
  talentClassificationIds: (number | string)[] | null | undefined,
  jobClassificationIds: (number | string)[] | null | undefined,
): boolean => {
  if (!talentClassificationIds || talentClassificationIds.length === 0) return true;
  if (!jobClassificationIds || jobClassificationIds.length === 0) return true;
  const jobIdSet = new Set(jobClassificationIds.map(String));
  return !talentClassificationIds.some((id) => jobIdSet.has(String(id)));
};

export const hasCompanyClassificationMismatch = (
  companyIndustryId1: number | null | undefined,
  companyClassificationIds1: (number | string)[] | null | undefined,
  jobIndustryId: number | null | undefined,
  jobClassificationIds: (number | string)[] | null | undefined,
): boolean => {
  if (!companyIndustryId1) return true;
  if (!jobIndustryId) return false;

  if (Number(companyIndustryId1) !== Number(jobIndustryId)) return true;

  if (!companyClassificationIds1 || companyClassificationIds1.length === 0) return true;
  if (!jobClassificationIds || jobClassificationIds.length === 0) return false;

  const jobIdSet = new Set(jobClassificationIds.map(String));
  return !companyClassificationIds1.every((id) => jobIdSet.has(String(id)));
};

export type TExpiryStatus = (typeof EXPIRY_STATUS)[keyof typeof EXPIRY_STATUS] | null;
export const getExpiryStatus = (dateStr: string | null | undefined): TExpiryStatus => {
  if (!dateStr) return null;
  const expiryDate = dayjs(dateStr);

  if (!expiryDate.isValid()) return null;

  const now = dayjs();

  if (expiryDate.isBefore(now, "day")) return EXPIRY_STATUS.EXPIRED;
  if (expiryDate.diff(now, "month", true) <= 1) return EXPIRY_STATUS.EXPIRING_SOON;

  return null;
};

export const createMaxDateTest = (refFieldKey: string, refFieldLabel: string) => ({
  name: `max-date-${refFieldKey}`,
  message: `${refFieldLabel}、またはそれ以前の日付を入力してください。`,
  test: function (this: { parent: Record<string, unknown> }, value: unknown) {
    const refDate = this.parent[refFieldKey];
    if (!isDayjs(value) || !isDayjs(refDate)) return true;
    return isDateSameOrAfter(refDate, value);
  },
});

/**
 * Date field definition with both label (UI) and key (API).
 */
type TDateFieldDef = {
  label: string;
  key: string;
};

/**
 * Unified mapping of status to date fields with both label and API key.
 * Single source of truth for date field clearing logic.
 */
const STATUS_DATE_FIELDS: Record<number, TDateFieldDef[]> = {
  [ECaseMatchStatus.PROPOSING]: [{ label: "推薦日", key: "proposalDate" }],
  [ECaseMatchStatus.UNDER_SELECTION]: [{ label: "面接日", key: "interviewDate" }],
  [ECaseMatchStatus.OFFER]: [{ label: "企業内定日", key: "companyOfferDate" }],
  [ECaseMatchStatus.OFFER_ACCEPTED]: [
    { label: "内定受諾日", key: "offerAcceptDate" },
    { label: "企業入社日（予定）", key: "joinDateExpected" },
  ],
  [ECaseMatchStatus.ALREADY_HIRED]: [{ label: "企業入社日", key: "companyJoiningDate" }],
};

/**
 * Get list of date field labels that will be affected when downgrading status.
 * Only returns labels for fields that ACTUALLY HAVE DATA (not empty fields).
 * Handles both consecutive (5→4) and non-consecutive (5→2) downgrades.
 */
export const getAffectedDateFieldsOnDowngrade = (
  currentStatus: number,
  newStatus: number,
  caseMatch?: Record<string, unknown> | null,
): string[] => {
  if (newStatus >= currentStatus) return [];

  const affectedFields: string[] = [];

  // Collect fields from all statuses between newStatus (exclusive) and currentStatus (inclusive)
  for (let status = newStatus + 1; status <= currentStatus; status++) {
    const fields = STATUS_DATE_FIELDS[status];
    if (fields) {
      fields.forEach(({ label, key }) => {
        // Only include if field actually has data (or if no caseMatch provided for backward compat)
        if (!caseMatch || caseMatch[key]) {
          affectedFields.push(label);
        }
      });
    }
  }

  return affectedFields;
};

/**
 * Get list of date field API keys that need to be cleared when downgrading status.
 * Returns API field keys for fields that should be set to null.
 */
export const getDateFieldKeysToClear = (currentStatus: number, newStatus: number): string[] => {
  if (newStatus >= currentStatus) return [];

  const fieldKeys: string[] = [];

  for (let status = newStatus + 1; status <= currentStatus; status++) {
    const fields = STATUS_DATE_FIELDS[status];
    if (fields) {
      fieldKeys.push(...fields.map((f) => f.key));
    }
  }

  return fieldKeys;
};

/**
 * CaseMatch object type for optional field validation.
 * Contains both top-level caseMatch fields and nested talent fields.
 */
type TCaseMatchForValidation = {
  talent?: Record<string, unknown> | null;
  fitNote?: unknown;
  joinDateExpected?: unknown;
  salesFee?: unknown;
  purchaseFee?: unknown;
} | null | undefined;

/**
 * Maps optional field label to checker function that returns true if field is empty.
 * Unified structure for both talent-level and caseMatch-level fields.
 */
type TOptionalFieldChecker = {
  label: string;
  isEmpty: (caseMatch: TCaseMatchForValidation) => boolean;
};

/**
 * Centralized mapping of status to optional fields requiring confirmation when empty.
 * Single source of truth for all optional field validation logic.
 */
const OPTIONAL_FIELD_CHECKERS: Record<number, TOptionalFieldChecker[]> = {
  [ECaseMatchStatus.UNDER_SELECTION]: [
    { label: "日本語能力", isEmpty: (cm) => !cm?.talent?.languageCertificates },
    { label: "日本語能力証明書", isEmpty: (cm) => !cm?.talent?.hasJapaneseCertificateFile },
    { label: "特定技能合格証等", isEmpty: (cm) => !cm?.talent?.hasSpecifiedSkillCertificateFile },
    { label: "職種/作業", isEmpty: (cm) => !cm?.talent?.majorId && !cm?.talent?.jobTypeId },
    { label: "在留カード", isEmpty: (cm) => !cm?.talent?.hasResidenceCardFiles },
  ],
  [ECaseMatchStatus.OFFER]: [
    { label: "適合度メモ", isEmpty: (cm) => !cm?.fitNote },
  ],
  [ECaseMatchStatus.OFFER_ACCEPTED]: [
    { label: "企業入社日（予定）", isEmpty: (cm) => !cm?.joinDateExpected },
    { label: "紹介料（売上）", isEmpty: (cm) => cm?.salesFee == null },
    { label: "紹介料（仕入）", isEmpty: (cm) => cm?.purchaseFee == null },
  ],
};

/**
 * Get list of optional field labels that are actually empty for the given case match.
 * Only returns fields that need user confirmation (are empty).
 * Uses centralized OPTIONAL_FIELD_CHECKERS mapping for all field validations.
 */
export const getEmptyOptionalFieldLabels = (
  newStatus: number,
  caseMatch: TCaseMatchForValidation,
): string[] => {
  const emptyFields: string[] = [];

  for (let status = 1; status <= newStatus; status++) {
    const checkers = OPTIONAL_FIELD_CHECKERS[status];
    if (checkers) {
      checkers.forEach(({ label, isEmpty }) => {
        if (isEmpty(caseMatch)) {
          emptyFields.push(label);
        }
      });
    }
  }

  return emptyFields;
};

/**
 * @deprecated Use getEmptyOptionalFieldLabels instead for accurate empty field checking.
 * Get list of optional field labels that should show confirmation if empty when downgrading.
 * These are fields without ※ mark that are required at the NEW status.
 */
export const getOptionalFieldLabelsForStatus = (newStatus: number): string[] => {
  const optionalFields: string[] = [];

  for (let status = 1; status <= newStatus; status++) {
    const checkers = OPTIONAL_FIELD_CHECKERS[status];
    if (checkers) {
      optionalFields.push(...checkers.map((c) => c.label));
    }
  }

  return optionalFields;
};
